import {
  SimulationCaseById,
  SimulationCaseList_simulationCaseList,
} from "__generated__/types";
import { useAuth } from "common/auth/Auth";
import { Button } from "common/buttons";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import Popup from "common/components/Popup";
import { Paragraph } from "common/components/Typography";
import { urls } from "common/urls";
import React, { Dispatch, PropsWithChildren, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Examination } from "./Examination";
import styles from "./Simulation.module.css";

type CaseProps = {
  data: SimulationCaseById;
  overrideNextCase?: SimulationCaseList_simulationCaseList[];
  setShowDiagnosis: Dispatch<React.SetStateAction<boolean>>;
};

export const Diagnosis: React.FC<PropsWithChildren<CaseProps>> = ({
  data,
  overrideNextCase,
  setShowDiagnosis,
}) => {
  const me = useAuth();
  const isAdmin = me.me?.menu.admin;
  const navigate = useNavigate();

  const [isQuizelementClickable, setIsQuizelementsClickable] = useState(true);
  const [clickedItems, setClickedItems] = useState(new Set<number>());
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  if (!data.simulationCaseById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const { diagnosis, treatment, referral, nextCase } = data.simulationCaseById;

  const handleQuizelementClick = (index: number) => {
    if (isQuizelementClickable) {
      setClickedItems((prevClickedItems) => {
        const newClickedItems = new Set(prevClickedItems);
        if (newClickedItems.has(index)) {
          newClickedItems.delete(index);
        } else {
          newClickedItems.add(index);
        }
        return newClickedItems;
      });
    }
  };

  const handleSubmitClick = () => {
    let correctAnswers = 0;
    let totalAnswers = 0;

    diagnosis?.forEach((item, index) => {
      if (item?.isCorrectOutcome) {
        totalAnswers++;
      }
      if (clickedItems.has(index) && item?.isCorrectOutcome) {
        correctAnswers++;
      } else if (clickedItems.has(index) && !item?.isCorrectOutcome) {
        correctAnswers--;
      }
    });

    treatment?.forEach((item, index) => {
      if (item?.isCorrectOutcome) {
        totalAnswers++;
      }
      if (clickedItems.has(index + 10) && item?.isCorrectOutcome) {
        correctAnswers++;
      } else if (clickedItems.has(index + 10) && !item?.isCorrectOutcome) {
        correctAnswers--;
      }
    });

    referral?.forEach((item, index) => {
      if (item?.isCorrectOutcome) {
        totalAnswers++;
      }
      if (clickedItems.has(index + 20) && item?.isCorrectOutcome) {
        correctAnswers++;
      } else if (clickedItems.has(index + 20) && !item?.isCorrectOutcome) {
        correctAnswers--;
      }
    });

    setScore(correctAnswers > 0 ? (correctAnswers / totalAnswers) * 100 : 0);
    setIsQuizelementsClickable(false);
    setIsSubmitClicked(true);
  };

  const handleReset = () => {
    setClickedItems(new Set<number>());
    setIsQuizelementsClickable(true);
    setIsSubmitClicked(false);
    setScore(null);
  };

  return (
    <div>
      <div className={styles.headerContainer}>
        <h3 className={styles.header}>Sett diagnose:</h3>
        <ul className={styles.quizContainer}>
          {diagnosis?.length ? (
            diagnosis.map((test, index) => (
              <li key={index} className={styles.quizElement}>
                <input
                  type="checkbox"
                  checked={clickedItems.has(index)}
                  onChange={() => {
                    handleQuizelementClick(index);
                  }}
                  className={styles.checkbox}
                />
                {test?.possibleOutcome ?? ""}
              </li>
            ))
          ) : (
            <Paragraph>En uforventet feil har oppstått.</Paragraph>
          )}
        </ul>
        <h3 className={styles.header}>Velg behandling:</h3>
        <ul className={styles.quizContainer}>
          {treatment?.length ? (
            treatment.map((test, index) => (
              <li key={index} className={styles.quizElement}>
                <input
                  type="checkbox"
                  checked={clickedItems.has(index + 10)}
                  onChange={() => {
                    handleQuizelementClick(index + 10);
                  }}
                  className={styles.checkbox}
                />
                {test?.possibleOutcome ?? ""}
              </li>
            ))
          ) : (
            <Paragraph>En uforventet feil har oppstått.</Paragraph>
          )}
        </ul>
        <h3 className={styles.header}>Vurder viderehenvisning:</h3>
        <ul className={styles.quizContainer}>
          {referral?.length ? (
            referral.map((test, index) => (
              <li key={index} className={styles.quizElement}>
                <input
                  type="checkbox"
                  checked={clickedItems.has(index + 20)}
                  onChange={() => {
                    handleQuizelementClick(index + 20);
                  }}
                  className={styles.checkbox}
                />
                {test?.possibleOutcome ?? ""}
              </li>
            ))
          ) : (
            <Paragraph>En uforventet feil har oppstått.</Paragraph>
          )}
        </ul>
      </div>

      <div className={styles.cancelOrSubmitContainer}>
        <Button
          onClick={() => {
            setShowDiagnosis(false);
          }}
        >
          Tilbake til case
        </Button>

        {isSubmitClicked ? (
          <div className={styles.tryAgain}>
            <Button onClick={handleReset}>Prøv på nytt</Button>
            {overrideNextCase && overrideNextCase.length > 0 ? (
              <Button
                onClick={() => {
                  navigate(
                    urls.simulationCase(overrideNextCase.shift()?.id ?? ""),
                    {
                      state: { cases: overrideNextCase },
                    },
                  );
                  setShowDiagnosis(false);
                }}
              >
                Gå til neste pasient
              </Button>
            ) : (
              nextCase?.id && (
                <Link to={urls.simulationCase(nextCase.id)}>
                  <Button>Gå til neste pasient</Button>
                </Link>
              )
            )}
          </div>
        ) : (
          <>
            <Popup
              trigger="click"
              content={<Examination data={data} displayReferral={false} />}
            >
              <div className={styles.popup}>
                <Button kind="secondary">Vis undersøkelser</Button>
              </div>
            </Popup>

            <div>
              <Button onClick={handleSubmitClick}>Sjekk svar ➢</Button>
            </div>
          </>
        )}
      </div>

      {isSubmitClicked && score !== null && (
        <div className={styles.scoreContainer}>
          <Paragraph>Du fikk {score.toFixed(2)}% korrekt!</Paragraph>
        </div>
      )}

      {isAdmin && (
        <Link to={urls.simulationDiagnosisAdmin(data.simulationCaseById.id)}>
          Rediger svaralternativer
        </Link>
      )}
    </div>
  );
};
